import { useEffect, useState, useRef } from 'react';
import { getTokenFromCookies } from '@/lib/cookies';
import { TOKEN_TYPE } from '@/lib/cookies';
import { useAuth } from '@/features/auth';
import { Loading } from '@/components/ui/loading';

interface AuthInitializerProps {
  children: React.ReactNode;
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { checkAuth } = useAuth();
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations (React StrictMode or re-renders)
    if (hasInitialized.current) {
      console.log('[AuthInitializer] Already initialized - skipping');
      return;
    }

    console.log('[AuthInitializer] useEffect triggered - first time');
    hasInitialized.current = true;

    const initAuth = async () => {
      try {
        console.log('[AuthInitializer] Starting auth initialization...');

        // Check if user has token in cookies
        const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
        const refreshToken = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN);
        console.log('[AuthInitializer] SSO token:', token || refreshToken ? 'Found' : 'Not found');

        if (token || refreshToken) {
          // If token exists, verify it with backend
          console.log('[AuthInitializer] Verifying JWT token with backend...');
          await checkAuth();
          console.log('[AuthInitializer] JWT token verified');
        }

        // Google Drive is intentionally not refreshed here. It is an independent,
        // user-initiated permission flow and must not be coupled to login.
      } catch (error) {
        console.error('[AuthInitializer] ❌ Auth initialization failed:', error);
      } finally {
        // Mark initialization as complete regardless of success/failure
        console.log('[AuthInitializer] Initialization complete');
        setIsInitializing(false);
      }
    };

    initAuth();
  }, [checkAuth]);

  // Show loading screen only during initial auth check
  if (isInitializing) {
    return <Loading text="Đang khởi tạo..." description="Đang tải thông tin của bạn" />;
  }

  return <>{children}</>;
};
